import lodash from 'lodash'
import { Logger } from 'wdc-cube'
import { ObjectProperty } from '../tcm_filter_manager'
import { JunctionMode, SelectFilterScope, SelectParcelFilterScope } from '../tcm_scopes'
import { FilterSelectOption, SelectFilter, SelectOperator } from '../tcm_service'
import { FilterPaneOwner, FilterPresenter } from './tcm_filter_types'

const LOG = Logger.get('TCM-SelectFilter')

let UID_GEN = 0

export class TcmPresenterForSelectFilter extends FilterPresenter<SelectFilterScope, FilterPaneOwner> {
    // Constructor

    constructor(owner: FilterPaneOwner, scope: SelectFilterScope, valueProperty: ObjectProperty<SelectFilter>) {
        super(owner, scope)
        this.valueProperty = valueProperty
        this.fetch = lodash.debounce(() => {
            this.owner.owner.fetchData('').catch(LOG.caught)
        }, 150)
    }

    async prepareFilter(order: number, label: string, selectOptions: FilterSelectOption[], visible = true) {
        super.prepare(order, label, visible)
        this.scope.getOptions = () => selectOptions
        await this.bindEvents()
    }

    // :: Fields

    private valueProperty: ObjectProperty<SelectFilter>

    private fetch: lodash.DebouncedFuncLeading<() => void>

    // :: Getters and Setters

    get owner() {
        return super.owner as FilterPaneOwner
    }

    get size() {
        return this.scope.conjunction.length + this.scope.disjunction.length
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override release(): void {
        this.fetch.cancel()
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    override publish() {
        this.valueProperty.clear()

        const emptyPredicate = (scope: SelectParcelFilterScope) => lodash.trim(scope.value) === ''
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        for (const parcelScope of [...this.scope.conjunction, ...this.scope.disjunction]) {
            const value = parcelScope.value.trim()
            if (!value) {
                continue
            }

            const filter: SelectFilter = {
                mode: parcelScope.junctionMode,
                operator: parcelScope.operator,
                value
            }

            this.valueProperty.add(filter)
        }
    }

    add(filter: SelectFilter) {
        const juntionScope = filter.mode == JunctionMode.AND ? this.addConjuntion() : this.addDisjuntion()
        juntionScope.operator = filter.operator
        juntionScope.value = filter.value ? `${filter.value}` : ''
    }

    addConjuntion() {
        const andScope = new SelectParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.operator = SelectOperator.is
        andScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, andScope))
        andScope.onValueChange = this.action(this.handleValueChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        andScope.update = this.update

        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new SelectParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.operator = SelectOperator.is
        orScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, orScope))
        orScope.onValueChange = this.action(this.handleValueChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        orScope.update = this.update
        this.scope.disjunction.push(orScope)
        return orScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }

    private async handleAddFilter(mode: JunctionMode) {
        this.mostrarItemAdicionado()

        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: SelectParcelFilterScope) {
        let found = true

        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
            found = true
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
            found = true
        }

        if (found && parcelScope.value) {
            this.fetch.cancel()
            this.fetch()
        }
    }

    private async handleOperatorChange(parcelScope: SelectParcelFilterScope, value: SelectOperator) {
        parcelScope.operator = value
        parcelScope.valueVisible = !(value === SelectOperator.is_blank || value === SelectOperator.is_not_blank)
    }

    private async handleValueChange(parcelScope: SelectParcelFilterScope, value: string) {
        parcelScope.value = value
    }

    private async handleEnterKeyPressed() {
        this.fetch.cancel()
        this.fetch()
    }

    apply(filters: SelectFilter[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            if (filter.value === null || filter.value === '' || filter.value === null || filter.value === undefined) {
                return
            }

            let itemScope: SelectParcelFilterScope
            if (filter.mode === JunctionMode.AND) {
                itemScope = this.addConjuntion()
            } else {
                itemScope = this.addDisjuntion()
            }

            if (filter.value) {
                itemScope.value = `${filter.value}`
            }

            itemScope.operator = filter.operator
        })
    }
}
